/* Warehouse Bold — publishes --wb-header-h: the height of whatever element is
 * actually pinned at the top of the viewport.
 *
 * ⚠ The theme's own --header-height cannot be used for this, for two separate
 * reasons that each produced a shipped bug:
 *
 *   Desktop — the WB header unboxes the section wrapper and <header> with
 *   `display: contents` so only the logo row is sticky. An unboxed element has
 *   no box, so header.js measured 0 and wrote it inline onto <html>, beating
 *   the stylesheet and dropping the section tab bar to the top of the viewport,
 *   behind the header.
 *
 *   Mobile — header.js runs its measurement through delayUntilInteraction, so
 *   --header-height is simply absent until the visitor's first interaction.
 *   Anything reading it before then silently falls back.
 *
 * Measuring the sticky element directly sidesteps both: on desktop that is
 * .header__top (88px), on mobile the whole .header-section (~141px). A
 * ResizeObserver keeps it honest across breakpoint changes, logo swaps and
 * announcement-bar rotations. Consumers are elsewhere in the document, so the
 * observer can never feed back into what it measures.
 *
 * The mobile header collapses its search row by TRANSFORM, deliberately
 * leaving its layout height alone (see the note in sections/header.liquid), so
 * this value stays at the expanded height by design. Consumers subtract
 * --search-row-height under html.header-search-collapsed, exactly as the
 * theme's own .sticky-element rules do.
 */
(() => {
  const root = document.documentElement;
  // Order matters: the first one that is sticky wins, from outermost in.
  const CANDIDATES = ['.header-section', 'header.header', '.header__top'];

  const publish = () => {
    let height = 0;
    for (let i = 0; i < CANDIDATES.length; i += 1) {
      const el = document.querySelector(CANDIDATES[i]);
      if (!el || getComputedStyle(el).position !== 'sticky') continue;
      // ⚠ Being sticky is not enough. On desktop .header-section keeps its
      // sticky position while `display: contents` removes its box, so it
      // matches first and measures 0 — take the height, not the match, as the
      // signal and keep walking inwards to .header__top.
      const h = el.getBoundingClientRect().height;
      if (h > 0) { height = h; break; }
    }
    // Never publish a zero: a missing value falls back to a sane 88px in CSS,
    // whereas a real 0 would park every consumer at the top of the viewport.
    if (height > 0) root.style.setProperty('--wb-header-h', Math.round(height) + 'px');
  };

  const start = () => {
    publish();
    if (window.ResizeObserver) {
      const ro = new ResizeObserver(publish);
      // ⚠ Observe EVERY candidate, not just .header-section. On desktop that
      // element is `display: contents` — it has no box, so a ResizeObserver on
      // it never fires even once. The value published at DOMContentLoaded then
      // stood forever, and at that moment the logo <img> has not loaded, so the
      // header measured 68px instead of its real 88px. Everything that parks
      // under the header inherited that 20px error: the PDP tab bar, the PDP
      // sticky rail, the Shop by Brand panel, every scroll anchor.
      CANDIDATES.forEach((sel) => {
        const el = document.querySelector(sel);
        if (el) ro.observe(el);
      });
    }
    // The breakpoint swap changes WHICH element is sticky without necessarily
    // resizing the observed box.
    window.addEventListener('resize', publish);
    // ⚠ The logo is an <img> and on desktop it IS the header's height (56px of
    // an 88px row). Its box only exists once the file has loaded, which is
    // after DOMContentLoaded — so without these two the first measurement is
    // taken of a header that has not finished being a header. `load` covers
    // the image, `fonts.ready` the text-logo fallback and the nav labels.
    window.addEventListener('load', publish);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(publish).catch(() => {});
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
